import React from 'react'
import PropTypes from 'prop-types'
import PriceMarker from './PriceMarker'
import ReloadButton from './ReloadButton'
import { Map as EPMap, withMap, Marker } from 'commons/js/components/Map'
import mapOptions from './mapOptions'
import { makeMarkersComparator } from './comparators'
import { getSearchBounds, getMapCenter, getBounds, getBoundsCenter } from './mapFunctions'

const MIN_ZOOM_FOR_SEARCH = 10

class Map extends React.PureComponent {
    static defaultProps = {
        stations: [],
        selected: null,
        marked: null,
        searchPoint: null,
        showMore: true,
        onMark: () => {},
        onSearch: () => {},
        disableAutoCenter: () => {},
        autoCenter: false,
    }

    static propTypes = {
        stations: PropTypes.array,
        searchPoint: PropTypes.shape({
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }),
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        isLoading: PropTypes.bool,
        selected: PropTypes.number,
        marked: PropTypes.number,
        showMore: PropTypes.bool,
        autoCenter: PropTypes.bool,
        onSearch: PropTypes.func,
        onMark: PropTypes.func,
        disableAutoCenter: PropTypes.func,
    }

    constructor (props) {
        super(props)

        const center = props.longitude && props.latitude
            ? getBoundsCenter(getSearchBounds({latitude: props.latitude, longitude: props.longitude}))
            : undefined

        this.state = {
            showReloadButton: false,
            loading: false,
            zoom: mapOptions.maxZoom,
            center,
            searchPoint: props.searchPoint,
            bounds: {},
            latitude: props.latitude,
            longitude: props.longitude,
        }

        this.onReload = this.onReload.bind(this)
        this.onZoomChanged = this.onZoomChanged.bind(this)
        this.onCenterChanged = this.onCenterChanged.bind(this)
        this.fitToBounds = this.fitToBounds.bind(this)
        this.performSearch = this.performSearch.bind(this)
    }

    componentDidMount () {
        this.fitToBounds()
    }

    componentDidUpdate (prevProps) {
        if (this.props.stations !== prevProps.stations) {
            this.fitToBounds()
        }
    }

    static getDerivedStateFromProps ({ latitude, longitude, searchPoint }, state) {
        const latitudeHasChanged = latitude !== state.latitude
        const longitudeHasChanged = longitude !== state.longitude
        const searchCenterHasChanged = searchPoint && (searchPoint !== state.searchPoint)

        const newState = {
            latitude,
            longitude,
        }

        if (searchCenterHasChanged) {
            newState.searchPoint = searchPoint
        }

        if (latitudeHasChanged || longitudeHasChanged) {
            newState.center = getBoundsCenter(getSearchBounds({latitude, longitude}))
        }

        if (newState.center || newState.searchPoint) {
            return newState
        }
        return null
    }

    fitToBounds () {
        if (!this.map) {
            return
        }

        const { stations, isLoading, autoCenter, latitude, longitude } = this.props

        if (isLoading) {
            return
        }

        if (!latitude || !longitude) {
            return
        }
        const center = { lat: latitude, lng: longitude }

        if (!stations.length) {
            this.setState(() => ({center}))
            return
        }

        if (autoCenter) {
            this.map.fitBounds(getBounds(stations, center))
        }

        const zoom = this.map.getZoom()

        this.setState(() => ({
            showReloadButton: false,
            zoom: Math.min(zoom, mapOptions.maxZoom),
        }))
    }

    performSearch () {
        const { center, zoom, bounds } = this.state

        this.props.onSearch({
            latitude: center.lat,
            longitude: center.lng,
            bounds: {
                ...bounds,
                zoom,
            },
        })
    }

    getMapBounds () {
        return {
            latN: this.map.getBounds().getNorthEast().lat(),
            lonE: this.map.getBounds().getNorthEast().lng(),
            latS: this.map.getBounds().getSouthWest().lat(),
            lonW: this.map.getBounds().getSouthWest().lng(),
        }
    }

    onReload () {
        this.setState(
            () => ({
                showReloadButton: false,
            }),
            () => (this.performSearch())
        )
    }

    onCenterChanged () {
        this.setState(() => ({
            center: getMapCenter(this.map),
            bounds: this.getMapBounds(),
            showReloadButton: true,
        }))
    }

    onZoomChanged () {
        this.setState(() => ({
            zoom: this.map.getZoom(),
            showReloadButton: true,
            bounds: this.getMapBounds(),
        }))
    }

    renderReloadButton () {
        if (!this.state.showReloadButton) {
            return null
        }

        return (
            <ReloadButton
              canSearch={this.state.zoom > MIN_ZOOM_FOR_SEARCH}
              onClick={this.onReload}
              loading={this.state.loading}
            />
        )
    }

    renderMarkers () {
        const { stations, marked, isLoading } = this.props

        if (isLoading) {
            return null
        }

        return stations
            .filter(({latitude, longitude}) => !!latitude && !!longitude)
            .sort(makeMarkersComparator(marked))
            .map((station, index) => this.renderMarker(station, index))
    }

    renderMarker (station, index) {
        const {
            selected,
            marked,
            onMark,
        } = this.props

        return (
            <PriceMarker
              zIndex={index}
              key={station.id}
              station={station}
              isSelected={selected === station.id}
              isMarked={marked === station.id}
              onClick={() => onMark(station.id)}
            />
        )
    }

    renderCenter () {
        const { searchPoint } = this.state

        if (!searchPoint) {
            return null
        }

        const { longitude: lng, latitude: lat } = this.state.searchPoint

        return (
            <Marker icon='/images/station/maps-pin.webp' zIndex={0} position={{lat, lng}} />
        )
    }

    render () {
        const { onMark, showMore, disableAutoCenter, autoCenter } = this.props
        const {zoom, center} = this.state

        return (
            <div>
                {showMore && this.renderReloadButton()}
                <EPMap
                  ref={(element) => { this.map = element }}
                  options={mapOptions}
                  zoom={zoom}
                  center={center}
                  onDragEnd={() => autoCenter && disableAutoCenter()}
                  onCenterChanged={this.onCenterChanged}
                  onZoomChanged={this.onZoomChanged}
                  onClick={() => onMark(null)}
                >
                    {this.renderCenter()}
                    {this.renderMarkers()}
                </EPMap>
            </div>
        )
    }
}

export { Map }

export default withMap(props => <Map {...props} />)
